import { useState } from 'react';
import { Mail, MessageSquare, User } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';

export const ContactUs = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Open mailto with pre-filled content
    const subject = encodeURIComponent(`Enquiry from ${form.name} - Jyotirlinga.info`);
    const body = encodeURIComponent(`From: ${form.name} (${form.email})\n\n${form.message}`);
    window.location.href = `mailto:jyotirlinga@outlook.in?subject=${subject}&body=${body}`;
    setStatus('Your email client has been opened. Please send the email to complete your enquiry.');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <SEOHead
        title="Contact Us"
        description="Get in touch with the Jyotirlinga.info team. We welcome your questions, feedback, and pilgrimage stories about the 12 sacred Jyotirlinga temples."
        keywords="contact jyotirlinga, feedback, pilgrimage support"
        canonical="https://jyotirlinga.info/contact"
      />
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Contact Us' }]} />
          <h1 className="font-cormorant text-4xl sm:text-5xl font-bold tracking-tighter text-temple-slate mt-8 mb-4">
            Contact Us
          </h1>
          <p className="font-outfit text-gray-600 mb-12 max-w-2xl">
            We'd love to hear from you — whether it's a suggestion, a pilgrimage story, corrections to temple information, or a business enquiry. Reach out below.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-cormorant text-2xl font-bold text-temple-slate mb-6">Get in Touch</h2>
              <div className="space-y-6 font-outfit">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-saffron-100 text-saffron-600 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 uppercase tracking-wider mb-1">Email</div>
                    <a href="mailto:jyotirlinga@outlook.in" className="text-temple-slate hover:text-saffron-600 font-medium">
                      jyotirlinga@outlook.in
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-saffron-100 text-saffron-600 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 uppercase tracking-wider mb-1">Response Time</div>
                    <p className="text-temple-slate">Within 2-3 business days</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-saffron-50 border border-saffron-100">
                <h3 className="font-cormorant text-lg font-bold text-temple-slate mb-2">Content Corrections</h3>
                <p className="font-outfit text-sm text-gray-700">
                  We strive for accuracy. If you notice any incorrect information about a temple's history, timings, or travel details, please let us know so we can update it.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-cormorant text-2xl font-bold text-temple-slate mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="block font-outfit text-sm text-gray-700 mb-2">Your Name</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 border border-orange-200 focus:outline-none focus:border-saffron-500 font-outfit"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block font-outfit text-sm text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 border border-orange-200 focus:outline-none focus:border-saffron-500 font-outfit"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block font-outfit text-sm text-gray-700 mb-2">Message</label>
                  <textarea
                    required
                    rows="5"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full px-4 py-3 border border-orange-200 focus:outline-none focus:border-saffron-500 font-outfit"
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-saffron-500 hover:bg-saffron-600 text-white py-3 font-outfit font-medium transition-colors"
                  data-testid="contact-submit-button"
                >
                  Send Message
                </button>
                {status && (
                  <p className="text-sm text-green-700 bg-green-50 border border-green-200 p-3 font-outfit" data-testid="contact-status">
                    {status}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
